import { useEffect, useRef, useState } from "react";
import { BRAND } from "@/lib/tokens";

interface DoneBoxProps {
  checked: boolean;
  /** Box edge length in px. The check + corner radius scale from it.
   * Defaults to 18 (the table cell). Mobile card uses 20. */
  size?: number;
}

/**
 * The canonical "done" checkbox VISUAL — a single primitive behind every
 * done-toggle (table CheckboxCell, mobile row card, subtask row) so the
 * check-draw + box pop stay pixel-identical everywhere. There used to be three
 * hand-rolled boxes; polishing one silently skipped the others.
 *
 * Purely presentational: the click target, `role`/`aria`, and the toggle call
 * belong to the caller's wrapper. This just renders the box + animated check.
 *
 * Motion is mount-safe (see index.css `.flow-donebox`): the check draws via a
 * stroke-dashoffset TRANSITION, so an already-done row paints instantly struck
 * and the draw runs only on a genuine false→true change. The box "pop" is a
 * one-shot keyframe applied ONLY for the transition into checked and cleared on
 * animationend, so it never replays on re-render/scroll. `prefers-reduced-
 * motion` disables both from one media query.
 */
export function DoneBox({ checked, size = 18 }: DoneBoxProps) {
  const prevChecked = useRef(checked);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    // Pop only on the newly-checked edge — never on mount, uncheck, or a
    // re-render at the same value.
    if (checked && !prevChecked.current) setPop(true);
    prevChecked.current = checked;
  }, [checked]);

  const radius = Math.max(4, Math.round(size * 0.28));
  const svg = Math.round(size * 0.72);

  return (
    <div
      className={pop ? "flow-donebox flow-donebox-pop" : "flow-donebox"}
      data-checked={checked}
      onAnimationEnd={() => setPop(false)}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: checked ? BRAND.blue : "var(--surface)",
        border: `1.5px solid ${checked ? BRAND.blue : "var(--scale-300)"}`,
      }}
    >
      <svg
        className="flow-donebox-check"
        width={svg}
        height={svg}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 12l4 4 10-12"
          stroke="#fff"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
